import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get button icon based on status
const getStatusIcon = (status) => {
  if (status === 'loading') return <Loader2 className="w-4 h-4 animate-spin" />;
  if (status === 'success') return <CheckCircle className="w-4 h-4" />;
  return <Mail className="w-4 h-4" />;
};

export const NewsletterSignup = ({ variant = 'default', className = '' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    
    try {
      const res = await axios.post(`${API_URL}/newsletter/subscribe`, {
        email,
        name: name || undefined,
        preferences: { agricultural: true, manufacturing: true, services: true }
      });
      
      setStatus('success');
      setMessage(res.data.message || 'Successfully subscribed!');
      setEmail('');
      setName('');
      
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    } catch (err) {
      setStatus('error');
      // Handle Pydantic validation errors (detail is array) vs string errors
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to subscribe. Please try again.';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      setMessage(errorMessage);
      
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  if (variant === 'minimal') {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`} data-testid="newsletter-form-minimal">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={status === 'loading' || status === 'success'}
          className="flex-1"
          data-testid="newsletter-email-input"
        />
        <Button 
          type="submit" 
          disabled={status === 'loading' || status === 'success'}
          className="bg-primary hover:bg-primary-hover text-white"
          data-testid="newsletter-submit-btn"
        >
          {getButtonContent()}
        </Button>
      </form>
    );
  }

  // Helper function to get button content - eliminates nested ternary
  function getButtonContent() {
    if (status === 'loading') {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    if (status === 'success') {
      return <CheckCircle className="w-4 h-4" />;
    }
    return 'Subscribe';
  }

  return (
    <div className={`relative ${className}`} data-testid="newsletter-section">
      <div className="bg-gradient-to-br from-primary/10 via-orange-50 to-accent/10 rounded-2xl p-8 border border-primary/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-heading text-xl text-charcoal">Stay Updated</h3>
            <p className="text-sm text-slate">Get monthly trade insights & opportunities</p>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex items-center gap-3 p-4 bg-accent/20 rounded-xl"
              data-testid="newsletter-success"
            >
              <CheckCircle className="w-6 h-6 text-accent" />
              <div>
                <p className="font-semibold text-charcoal">You're subscribed!</p>
                <p className="text-sm text-slate">{message}</p>
              </div>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={handleSubmit}
              className="space-y-3"
              data-testid="newsletter-form"
            >
              <Input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={status === 'loading'}
                className="bg-white"
                data-testid="newsletter-name-input"
              />
              <Input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={status === 'loading'}
                className="bg-white"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit" 
                disabled={status === 'loading'}
                className="w-full bg-primary hover:bg-primary-hover text-white font-semibold gap-2"
                data-testid="newsletter-submit-btn"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Subscribe to Newsletter
                  </>
                )}
              </Button>
              {status === 'error' && (
                <p className="text-sm text-red-600 text-center" data-testid="newsletter-error">{message}</p>
              )}
              <p className="text-xs text-slate text-center">
                AI-curated insights on African trade opportunities. Unsubscribe anytime.
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Footer newsletter inline version
export const NewsletterInline = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    
    try {
      await axios.post(`${API_URL}/newsletter/subscribe`, { email });
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="newsletter-inline">
      <div className="flex gap-2">
        <Input
          type="email"
          placeholder="Your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={status !== 'idle'}
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
          data-testid="newsletter-inline-email"
        />
        <Button 
          type="submit" 
          disabled={status !== 'idle'}
          className="bg-primary hover:bg-primary-hover text-white shrink-0"
          data-testid="newsletter-inline-submit"
        >
          {getStatusIcon(status)}
        </Button>
      </div>
      {status === 'success' && (
        <p className="text-xs text-accent">Subscribed successfully!</p>
      )}
      {status === 'error' && (
        <p className="text-xs text-red-400">Failed to subscribe. Try again.</p>
      )}
    </form>
  );
};
